// src/pages/AudiencesPage.tsx
import React, { useState, useEffect } from "react";
import { Plus, Search, Filter, Zap, ChevronDown, ChevronUp, X, Play, Save, Copy, Trash2, ArrowRight, Tv, Smartphone, Globe, DollarSign } from "lucide-react";
import { clsx } from "clsx";
import AudienceSizeEstimator from "@/components/ui/AudienceSizeEstimator";
import { AUDIENCES, FILTER_DIMENSIONS } from "@/data";
import { intentCategories, intentTopics } from "@/data/intent";
import { financialSignals, creditTiers, incomeRanges } from "@/data/financial";
import { identityGraphStats } from "@/data/identityGraph";
import { POI_LIBRARY } from "@/data/mobility";
import { MapPin, Navigation } from "lucide-react";
import type { AudienceSegment } from "@/types";
import { format, parseISO } from "date-fns";

const CHANNEL_LABELS: Record<string, string> = { oo: "O&O", display: "Display", ctv: "CTV", video: "Video", social: "Social" };
const CHANNEL_COLORS: Record<string, string> = { oo: "badge-navy", display: "badge-blue", ctv: "badge-cyan", video: "badge-green", social: "badge-yellow" };
const STATUS_STYLES: Record<string, string> = { active: "badge-green", draft: "badge-yellow", archived: "badge-slate" };
const REFRESH_LABELS: Record<string, string> = { real_time: "Real-time", daily: "Daily", weekly: "Weekly", manual: "Manual" };

interface ActiveFilter { dimensionId: string; dimensionLabel: string; value: string; label: string; impact: number; type: string; }

function fmt(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

function computeSize(filters: ActiveFilter[]): number {
  if (filters.length === 0) return 0;
  const intentFilters = filters.filter(f => f.type === "intent");
  const geoFilters    = filters.filter(f => f.type === "geo");
  const demoFilters   = filters.filter(f => f.type === "demo");
  const finFilters    = filters.filter(f => f.type === "financial");
  let base = 0;
  if (intentFilters.length > 0) {
    base = intentFilters.reduce((sum, f) => sum + f.impact, 0);
    if (intentFilters.length > 1) base = Math.round(base * 0.65);
  } else {
    base = 12_000_000;
  }
  let geoMult  = geoFilters.length  > 0 ? Math.max(...geoFilters.map(f => f.impact))                    : 1;
  let demoMult = demoFilters.length > 0 ? demoFilters.reduce((s, f) => s + f.impact, 0)                 : 1;
  let finMult  = finFilters.length  > 0 ? Math.min(...finFilters.map(f => f.impact / 498_000_000))       : 1;
  return Math.round(base * geoMult * (demoFilters.length > 0 ? demoMult : 1) * (finFilters.length > 0 ? finMult * 3 : 1));
}

// ── Cross-device reach estimator ──────────────────────────────
function estimateDevices(personCount: number) {
  const maidRate  = identityGraphStats.mobileDevices.total  / identityGraphStats.individuals;
  const ctvRate   = identityGraphStats.ctv.linkedToIdentity / identityGraphStats.individuals;
  const ipRate    = identityGraphStats.ipAddresses.total    / identityGraphStats.individuals;
  return {
    maids: Math.round(personCount * maidRate * 1.18),   // avg 1.18 devices/person
    ctv:   Math.round(personCount * ctvRate  * 1.4),    // avg 1.4 CTV screens/household
    ip:    Math.round(personCount * ipRate   * 0.3),    // residential IPs only
  };
}

export default function AudiencesPage() {
  const [activeFilters, setActiveFilters]     = useState<ActiveFilter[]>([]);
  const [expandedDims, setExpandedDims]       = useState<string[]>(["intent_category"]);
  const [expandedCat, setExpandedCat]         = useState<string | null>(null);
  const [estimatedSize, setEstimatedSize]     = useState(0);
  const [isEstimating, setIsEstimating]       = useState(false);
  const [segmentName, setSegmentName]         = useState("");
  const [searchQuery, setSearchQuery]         = useState("");
  const [savedSuccess, setSavedSuccess]       = useState(false);
  const [selectedPOI,     setSelectedPOI]      = useState<string | null>(null);
  const [dwellMin,        setDwellMin]          = useState(0);
  const [visitWindow,     setVisitWindow]       = useState("30");
  const [crossShopFilter, setCrossShopFilter]   = useState<string | null>(null);
  const [wfhToggle,       setWfhToggle]         = useState(false);
  const [tab, setTab]                         = useState<"builder"|"saved">("builder");
  const [selectedCreditTiers, setSelectedCreditTiers] = useState<string[]>([]);
  const [selectedIncome, setSelectedIncome]   = useState<string | null>(null);
  const [selectedFinSignals, setSelectedFinSignals]   = useState<string[]>([]);

  useEffect(() => {
    setIsEstimating(true);
    const t = setTimeout(() => { setEstimatedSize(computeSize(activeFilters)); setIsEstimating(false); }, 400);
    return () => clearTimeout(t);
  }, [activeFilters]);

  const toggleFilter = (dimId: string, dimLabel: string, value: string, label: string, impact: number, type: string) => {
    setActiveFilters(prev => {
      const exists = prev.find(f => f.dimensionId === dimId && f.value === value);
      if (exists) return prev.filter(f => !(f.dimensionId === dimId && f.value === value));
      return [...prev, { dimensionId: dimId, dimensionLabel: dimLabel, value, label, impact, type }];
    });
  };

  const removeFilter = (dimId: string, value: string) => setActiveFilters(prev => prev.filter(f => !(f.dimensionId === dimId && f.value === value)));
  const clearFilters = () => { setActiveFilters([]); setSegmentName(""); setSelectedCreditTiers([]); setSelectedIncome(null); setSelectedFinSignals([]); };
  const toggleDim    = (id: string) => setExpandedDims(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);

  const handleSave = () => {
    if (!segmentName || activeFilters.length === 0) return;
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2500);
  };

  const filteredAudiences = AUDIENCES.filter(a =>
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.filters.some(f => f.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const breakdown = activeFilters.filter(f => f.type === "intent").slice(0, 3).map((f, i) => ({
    label: f.label, value: Math.round(f.impact * (i === 0 ? 1 : 0.4)),
    color: ["bg-blue", "bg-cyan", "bg-navy"][i],
  }));

  const deviceEstimate = estimateDevices(estimatedSize);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Audience Builder</h1>
          <p className="page-subtitle">Build, estimate, and activate precision audience segments</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab("builder")} className={clsx("btn-sm btn", tab === "builder" ? "btn-primary" : "btn-secondary")}>
            <Plus size={13} /> New Segment
          </button>
          <button onClick={() => setTab("saved")} className={clsx("btn-sm btn", tab === "saved" ? "btn-primary" : "btn-secondary")}>
            Saved Segments ({AUDIENCES.length})
          </button>
        </div>
      </div>

      {tab === "builder" ? (
        <div className="grid grid-cols-12 gap-4">
          {/* ── Filter Sidebar ── */}
          <div className="col-span-3 space-y-3">

            {/* Intent Category Taxonomy */}
            <div className="card">
              <p className="section-label">Intent Taxonomy</p>
              <p className="text-2xs text-slate mb-2">{intentCategories.length} categories · 8,305 topics · 25.5B weekly signals</p>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {intentCategories.map(cat => (
                  <div key={cat.category}>
                    <button onClick={() => setExpandedCat(expandedCat === cat.category ? null : cat.category)}
                      className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                        <span className="text-xs font-medium text-gray-800 truncate">{cat.category}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-2xs text-slate tabular">{fmt(cat.volume7d)}</span>
                        {expandedCat === cat.category ? <ChevronUp size={11} className="text-slate" /> : <ChevronDown size={11} className="text-slate" />}
                      </div>
                    </button>
                    {expandedCat === cat.category && (
                      <div className="ml-4 mt-1 space-y-0.5 pb-1">
                        <div className="flex justify-between px-2 py-1 text-2xs text-slate border-b border-gray-100 mb-1">
                          <span>{cat.topics} topics</span>
                          <span>{fmt(cat.volume30d)} / 30d</span>
                        </div>
                        {intentTopics.filter(t => t.category === cat.category).slice(0, 5).map(topic => {
                          const isActive = activeFilters.some(f => f.value === topic.id);
                          return (
                            <button key={topic.id}
                              onClick={() => toggleFilter("intent_topic", cat.category, topic.id, topic.topic, topic.count7d, "intent")}
                              className={clsx("w-full flex items-center justify-between px-2 py-1 rounded text-2xs transition-all",
                                isActive ? "bg-blue/10 text-blue font-semibold" : "text-gray-600 hover:bg-gray-50")}>
                              <span className="truncate text-left pr-1">{topic.topic}</span>
                              <span className="tabular flex-shrink-0">{fmt(topic.count7d)}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Standard Filters */}
            <div className="card">
              <p className="section-label">Filters</p>
              <div className="space-y-2">
                {FILTER_DIMENSIONS.filter(d => d.type !== "intent").map(dim => (
                  <div key={dim.id} className="border border-gray-100 rounded-lg overflow-hidden">
                    <button onClick={() => toggleDim(dim.id)} className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
                      <span className="text-xs font-semibold text-navy">{dim.label}</span>
                      <div className="flex items-center gap-1.5">
                        {activeFilters.filter(f => f.dimensionId === dim.id).length > 0 && (
                          <span className="w-4 h-4 rounded-full bg-blue text-white text-2xs flex items-center justify-center font-bold">
                            {activeFilters.filter(f => f.dimensionId === dim.id).length}
                          </span>
                        )}
                        {expandedDims.includes(dim.id) ? <ChevronUp size={13} className="text-slate" /> : <ChevronDown size={13} className="text-slate" />}
                      </div>
                    </button>
                    {expandedDims.includes(dim.id) && (
                      <div className="px-3 py-2 space-y-1">
                        {dim.options.map(opt => {
                          const isActive = activeFilters.some(f => f.dimensionId === dim.id && f.value === opt.value);
                          return (
                            <button key={opt.value} onClick={() => toggleFilter(dim.id, dim.label, opt.value, opt.label, opt.audienceImpact, dim.type)}
                              className={clsx("w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs transition-all duration-150",
                                isActive ? "bg-blue/10 text-blue font-semibold" : "text-gray-700 hover:bg-gray-50")}>
                              <span>{opt.label}</span>
                              <span className={clsx("text-2xs tabular", isActive ? "text-blue" : "text-slate")}>
                                {typeof opt.audienceImpact === "number" && opt.audienceImpact > 1
                                  ? fmt(opt.audienceImpact) : `${(opt.audienceImpact * 100).toFixed(0)}%`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Financial Propensity */}
            <div className="card">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign size={14} className="text-navy" />
                <p className="section-label mb-0">Financial Propensity</p>
              </div>

              {/* Credit Tier */}
              <div className="mb-3">
                <p className="text-2xs font-semibold text-slate uppercase tracking-wide mb-2">Credit Tier</p>
                <div className="space-y-1">
                  {creditTiers.map(tier => {
                    const isActive = selectedCreditTiers.includes(tier.range);
                    return (
                      <button key={tier.range}
                        onClick={() => {
                          const next = isActive ? selectedCreditTiers.filter(t => t !== tier.range) : [...selectedCreditTiers, tier.range];
                          setSelectedCreditTiers(next);
                          if (!isActive) toggleFilter("credit_tier", "Credit Tier", tier.range, `${tier.label} (${tier.range})`, tier.count, "financial");
                          else removeFilter("credit_tier", tier.range);
                        }}
                        className={clsx("w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs transition-all",
                          isActive ? "bg-blue/10 text-blue font-semibold" : "text-gray-700 hover:bg-gray-50")}>
                        <span>{tier.label} ({tier.range})</span>
                        <span className="text-2xs tabular text-slate">{fmt(tier.count)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Income Range */}
              <div className="mb-3">
                <p className="text-2xs font-semibold text-slate uppercase tracking-wide mb-2">Household Income</p>
                <select className="input text-xs"
                  value={selectedIncome ?? ""}
                  onChange={e => {
                    const val = e.target.value;
                    if (selectedIncome) removeFilter("income_range", selectedIncome);
                    setSelectedIncome(val || null);
                    if (val) {
                      const found = incomeRanges.find(r => r.range === val);
                      if (found) toggleFilter("income_range", "Income Range", val, val, found.count, "financial");
                    }
                  }}>
                  <option value="">Any income range</option>
                  {incomeRanges.map(r => (
                    <option key={r.range} value={r.range}>{r.range} — {fmt(r.count)}</option>
                  ))}
                </select>
              </div>

              {/* Propensity Signals */}
              <div>
                <p className="text-2xs font-semibold text-slate uppercase tracking-wide mb-2">Propensity Signals</p>
                <div className="space-y-1">
                  {financialSignals.slice(0, 4).map(sig => {
                    const isActive = selectedFinSignals.includes(sig.id);
                    return (
                      <button key={sig.id}
                        onClick={() => {
                          const next = isActive ? selectedFinSignals.filter(s => s !== sig.id) : [...selectedFinSignals, sig.id];
                          setSelectedFinSignals(next);
                          if (!isActive) toggleFilter("fin_signal", "Financial Signal", sig.id, sig.name, sig.coverage, "financial");
                          else removeFilter("fin_signal", sig.id);
                        }}
                        className={clsx("w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs transition-all",
                          isActive ? "bg-blue/10 text-blue font-semibold" : "text-gray-700 hover:bg-gray-50")}>
                        <span className="truncate text-left pr-1">{sig.name}</span>
                        <span className="text-2xs tabular text-slate flex-shrink-0">{fmt(sig.coverage)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* ── Canvas ── */}
          <div className="col-span-6 space-y-4">
            {/* Active filters */}
            <div className="card min-h-[100px]">
              <div className="flex items-center justify-between mb-3">
                <p className="section-label mb-0">Active Filters</p>
                {activeFilters.length > 0 && (
                  <button onClick={clearFilters} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors">
                    <X size={11} /> Clear all
                  </button>
                )}
              </div>
              {activeFilters.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-4 text-center">
                  <Filter size={20} className="text-gray-200 mb-2" />
                  <p className="text-sm text-slate">No filters applied</p>
                  <p className="text-xs text-gray-400 mt-0.5">Select from the left panel to build your audience</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {activeFilters.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-blue/8 border border-blue/20 text-blue rounded-full px-3 py-1 text-xs font-medium">
                      <span className="text-slate text-2xs">{f.dimensionLabel}:</span>
                      <span>{f.label}</span>
                      <button onClick={() => removeFilter(f.dimensionId, f.value)} className="ml-0.5 hover:text-red-500 transition-colors">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Segment details */}
            <div className="card">
              <p className="section-label">Segment Details</p>
              <div className="space-y-3">
                <div>
                  <label className="input-label">Segment Name</label>
                  <input className="input" placeholder="e.g. Power Tool In-Market — High Intent" value={segmentName} onChange={e => setSegmentName(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="input-label">Refresh Schedule</label>
                    <select className="input"><option>Daily</option><option>Weekly</option><option>Real-time</option><option>Manual</option></select>
                  </div>
                  <div>
                    <label className="input-label">Attribution Model</label>
                    <select className="input"><option>Data-driven</option><option>Last touch</option><option>Linear</option><option>Time decay</option></select>
                  </div>
                </div>
                <div>
                  <label className="input-label">Activate on Channels</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {["Display","O&O","CTV","Video"].map(ch => (
                      <label key={ch} className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="checkbox" className="rounded" defaultChecked={ch === "Display"} />
                        <span>{ch}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={!segmentName || activeFilters.length === 0}
                className={clsx("btn flex-1", savedSuccess ? "btn-accent" : "btn-primary")}>
                {savedSuccess ? <><Zap size={14} /> Saved!</> : <><Save size={14} /> Save Segment</>}
              </button>
              <button disabled={estimatedSize === 0} className="btn btn-secondary gap-2"><Play size={14} /> Activate</button>
              <button className="btn btn-ghost"><Copy size={14} /></button>
            </div>
          </div>

          {/* ── Right Panel ── */}
          <div className="col-span-3 space-y-4">
            <AudienceSizeEstimator size={estimatedSize} isLoading={isEstimating} breakdown={breakdown.length > 0 ? breakdown : undefined} />

            {/* Cross-Device Reach Estimator */}
            {estimatedSize > 0 && (
              <div className="card border border-navy/10">
                <div className="flex items-center gap-2 mb-3">
                  <Zap size={14} className="text-cyan" />
                  <p className="text-xs font-semibold text-navy">Cross-Device Reach</p>
                </div>
                <p className="text-2xs text-slate mb-3">
                  Your audience of <span className="font-semibold text-navy">{fmt(estimatedSize)}</span> people can be reached across:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-blue/5 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Smartphone size={13} className="text-blue" />
                      <span className="text-xs text-gray-700">Mobile Devices</span>
                    </div>
                    <span className="text-xs font-bold text-blue tabular">{fmt(deviceEstimate.maids)}</span>
                  </div>
                  <div className="flex items-center justify-between bg-navy/5 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Tv size={13} className="text-navy" />
                      <span className="text-xs text-gray-700">CTV Screens</span>
                    </div>
                    <span className="text-xs font-bold text-navy tabular">{fmt(deviceEstimate.ctv)}</span>
                  </div>
                  <div className="flex items-center justify-between bg-cyan/5 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Globe size={13} className="text-cyan" />
                      <span className="text-xs text-gray-700">IP Addresses</span>
                    </div>
                    <span className="text-xs font-bold text-cyan tabular">{fmt(deviceEstimate.ip)}</span>
                  </div>
                </div>
                <p className="text-2xs text-slate mt-2">Based on SIG cross-device graph · 91% match rate</p>
              </div>
            )}

            {/* Similar segments */}
            <div className="card">
              <p className="section-label">Similar Segments</p>
              <div className="space-y-2">
                {AUDIENCES.slice(0, 3).map(seg => (
                  <div key={seg.id} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-xs font-medium text-gray-800 truncate">{seg.name}</p>
                      <p className="text-2xs text-slate">{fmt(seg.estimatedSize)}</p>
                    </div>
                    <span className="text-xs font-semibold text-slate tabular">
                      {Math.floor(Math.random() * 40 + 10)}% overlap
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {/* Place & Mobility Filter */}
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-navy" />
                  <p className="section-label mb-0">Place &amp; Mobility</p>
                </div>
                <span className="badge badge-cyan text-2xs">MRN Mobility Intelligence</span>
              </div>
              <div className="mb-3">
                <p className="text-2xs font-semibold text-slate uppercase tracking-wide mb-2">POI Visitor Targeting</p>
                <select className="input text-xs mb-2" value={selectedPOI ?? ""} onChange={e => { const val = e.target.value; if (selectedPOI) removeFilter('poi_visitor', selectedPOI); setSelectedPOI(val || null); if (val) { const poi = POI_LIBRARY.find(p => p.poi_id === val); if (poi) toggleFilter('poi_visitor', 'POI Visitor', val, 'Visited: '+poi.name, 180000, 'mobility'); } }}>
                  <option value="">Select a location...</option>
                  <optgroup label="My Locations">{POI_LIBRARY.filter(p => p.retailer).map(p => (<option key={p.poi_id} value={p.poi_id}>{p.name}</option>))}</optgroup>
                  <optgroup label="Category-Adjacent">{POI_LIBRARY.filter(p => p.poi_id.startsWith('adj')).map(p => (<option key={p.poi_id} value={p.poi_id}>{p.name}</option>))}</optgroup>
                </select>
                <div className="flex gap-1">{['7','30','90'].map(w => (<button key={w} onClick={() => setVisitWindow(w)} className={clsx('flex-1 text-2xs py-1 rounded-md border transition-all', visitWindow === w ? 'bg-blue text-white border-blue' : 'bg-white text-slate border-gray-200')}>{w}d</button>))}</div>
              </div>
              <div className="mb-3">
                <p className="text-2xs font-semibold text-slate uppercase tracking-wide mb-1">Min Dwell Time</p>
                <div className="flex items-center justify-between mb-1"><span className="text-2xs text-slate">Any duration</span><span className="text-xs font-bold text-navy tabular">{dwellMin > 0 ? dwellMin+'+ min' : 'Any'}</span></div>
                <input type="range" min={0} max={90} step={5} value={dwellMin} onChange={e => setDwellMin(Number(e.target.value))} className="w-full accent-blue cursor-pointer" />
              </div>
              <div className="mb-3">
                <p className="text-2xs font-semibold text-slate uppercase tracking-wide mb-2">Cross-Shopping Pattern</p>
                <select className="input text-xs" value={crossShopFilter ?? ""} onChange={e => { const val = e.target.value; if (crossShopFilter) removeFilter('cross_shop', crossShopFilter); setCrossShopFilter(val || null); if (val) toggleFilter('cross_shop', 'Cross-Shop', val, val, 240000, 'mobility'); }}>
                  <option value="">Any shopping pattern...</option>
                  <optgroup label="Visits Competitor"><option value="visits_home_depot">Visits Home Depot</option><option value="visits_lowes">Visits Lowe's</option><option value="visits_whole_foods">Visits Whole Foods</option><option value="visits_nike">Visits Nike</option></optgroup>
                  <optgroup label="Visits Category-Adjacent"><option value="visits_costco">Visits Costco</option><option value="visits_target">Visits Target</option><option value="visits_ikea">Visits IKEA</option></optgroup>
                </select>
              </div>
              <div className="mb-3">
                <p className="text-2xs font-semibold text-slate uppercase tracking-wide mb-2">Trade Area Residence</p>
                <select className="input text-xs" onChange={e => { const val = e.target.value; if (val) toggleFilter('trade_area', 'Trade Area', val, 'Lives near: '+val, 320000, 'mobility'); }}>
                  <option value="">Any location...</option>
                  <option value="mhw-001">Near Midwest Hardware — Lincoln Square</option>
                  <option value="mhw-002">Near Midwest Hardware — Evanston</option>
                  <option value="fmg-001">Near FreshMart — Wicker Park</option>
                  <option value="sna-001">Near StyleNow — Magnificent Mile</option>
                </select>
              </div>
              <div className="flex items-center justify-between py-2 border-t border-gray-100">
                <div><p className="text-xs font-medium text-gray-800">Work-from-Home</p><p className="text-2xs text-slate">Daytime home presence signal</p></div>
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-2xs text-slate bg-gray-50 rounded-lg px-2.5 py-1.5">
                <Navigation size={10} className="text-cyan flex-shrink-0" />
                <span>Daily US mobility data · tied to MRN Person ID · privacy-safe</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
              <input className="input pl-9 w-72" placeholder="Search segments..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <button className="btn-secondary btn-sm flex items-center gap-1.5"><Filter size={12} /> Filter</button>
              <button onClick={() => setTab("builder")} className="btn-primary btn-sm flex items-center gap-1.5"><Plus size={12} /> New Segment</button>
            </div>
          </div>
          <table className="table-base">
            <thead><tr><th>Segment</th><th>Est. Size</th><th>Filters</th><th>Channels</th><th>Refresh</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {filteredAudiences.map(seg => (
                <tr key={seg.id} className="cursor-pointer">
                  <td>
                    <p className="font-medium text-gray-800">{seg.name}</p>
                    <p className="text-xs text-slate">{format(parseISO(seg.updatedAt), "MMM d, yyyy")}</p>
                  </td>
                  <td className="tabular font-semibold text-navy">
                    {seg.estimatedSize >= 1_000_000 ? `${(seg.estimatedSize/1_000_000).toFixed(1)}M` : `${(seg.estimatedSize/1_000).toFixed(0)}K`}
                  </td>
                  <td>
                    <div className="space-y-0.5">
                      {seg.filters.slice(0,2).map((f,i) => <p key={i} className="text-xs text-slate truncate max-w-[180px]">{f}</p>)}
                      {seg.filters.length > 2 && <p className="text-xs text-blue">+{seg.filters.length-2} more</p>}
                    </div>
                  </td>
                  <td><div className="flex gap-1 flex-wrap">{seg.channels.map(ch => <span key={ch} className={clsx("badge", CHANNEL_COLORS[ch])}>{CHANNEL_LABELS[ch]}</span>)}</div></td>
                  <td><span className="text-xs text-slate">{REFRESH_LABELS[seg.refreshSchedule]}</span></td>
                  <td><span className={clsx("badge", STATUS_STYLES[seg.status])}>{seg.status.charAt(0).toUpperCase()+seg.status.slice(1)}</span></td>
                  <td>
                    <div className="flex gap-1">
                      <button className="btn-ghost btn-sm p-1.5"><Play size={12} /></button>
                      <button className="btn-ghost btn-sm p-1.5"><Copy size={12} /></button>
                      <button className="btn-ghost btn-sm p-1.5 text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}